import React, { useState } from 'react';
import Spinner from '../Spinner';
import MatchTypeSelector from '../MatchTypeSelector';
import { PostGameSeasonGame } from '../../types/postGameSeason';

interface PostGameSeasonTableProps {
  games: PostGameSeasonGame[];
  sportLabel: string;
  season: number | null;
  savingId: number | null;
  rowError: string | null;
  onSave: (row: PostGameSeasonGame, matchTypesText: string) => void;
}

function PostGameSeasonTable({
  games,
  sportLabel,
  season,
  savingId,
  rowError,
  onSave,
}: PostGameSeasonTableProps) {
  const [editingId, setEditingId] = useState<number | null>(null);
  const [draft, setDraft] = useState('');

  const startEdit = (row: PostGameSeasonGame) => {
    setEditingId(row.id);
    setDraft(row.matchTypes?.join(', ') ?? '');
  };

  const cancelEdit = () => {
    setEditingId(null);
    setDraft('');
  };

  if (games.length === 0) {
    return (
      <div className="rounded-[22px] border border-dashed border-slate-300 bg-slate-50 px-6 py-10 text-center text-sm text-slate-500">
        No games found for {sportLabel}
        {season ? ` in ${season}` : ''}.
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {rowError ? (
        <div className="rounded-lg border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
          {rowError}
        </div>
      ) : null}

      <div className="overflow-hidden rounded-[22px] border border-slate-200 bg-white shadow-[0_20px_44px_-38px_rgba(15,23,42,0.5)]">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-slate-200 text-left text-sm">
            <thead className="bg-slate-50">
              <tr>
                <th className="px-5 py-4 text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">
                  Game date
                </th>
                <th className="px-5 py-4 text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">
                  Opponent
                </th>
                <th className="px-5 py-4 text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">
                  Source
                </th>
                <th className="px-5 py-4 text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">
                  Match types
                </th>
                <th className="px-5 py-4 text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {games.map((row) => {
                const isEditing = editingId === row.id;
                const isSaving = savingId === row.id;

                return (
                  <tr key={row.id} className="hover:bg-slate-50/80">
                    <td className="whitespace-nowrap px-5 py-4 font-medium text-[#16233d]">
                      {row.gameDate || '—'}
                    </td>
                    <td className="px-5 py-4 text-[#60718f]">{row.opponentTeamName || '—'}</td>
                    <td className="whitespace-nowrap px-5 py-4 text-[#60718f]">
                      {row.dataSource || '—'}
                    </td>
                    <td className="px-5 py-4 text-[#60718f]">
                      {isEditing ? (
                        <MatchTypeSelector
                          initialValue={draft}
                          disabled={isSaving}
                          onChange={setDraft}
                        />
                      ) : (
                        row.matchTypeLabel || '—'
                      )}
                    </td>
                    <td className="whitespace-nowrap px-5 py-4">
                      {isEditing ? (
                        <div className="flex items-center gap-2">
                          <button
                            type="button"
                            disabled={isSaving}
                            onClick={() => onSave(row, draft)}
                            className="text-sm font-semibold text-sky-600 hover:text-sky-800 disabled:opacity-50"
                          >
                            {isSaving ? <Spinner size="sm" /> : 'Save'}
                          </button>
                          <button
                            type="button"
                            disabled={isSaving}
                            onClick={cancelEdit}
                            className="text-sm text-slate-500 hover:text-slate-700 disabled:opacity-50"
                          >
                            Cancel
                          </button>
                        </div>
                      ) : (
                        <button
                          type="button"
                          onClick={() => startEdit(row)}
                          className="text-sm font-semibold text-sky-600 hover:text-sky-800"
                        >
                          Edit
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

export default PostGameSeasonTable;
